import { fetchers } from "@/services/api/fetchers";
import { AddFolder, AddFolderRes, FetchedFolder, UpdateFolderRes } from "@/services/api/fetchers/folders";
import { performOptimisticAction } from "@/shared/utils/store";
import { StateCreator } from "zustand";
import { BoundStore } from "./index";

function getDescendantFolderIds(folders: FetchedFolder[], folderId: number): number[] {
  const descendants: number[] = [];
  const stack = [folderId];
  while (stack.length > 0) {
    const currentId = stack.pop()!;
    const children = folders.filter((f) => f.parent_id === currentId);
    children.forEach((child) => {
      descendants.push(child.id);
      stack.push(child.id);
    });
  }
  return descendants;
}

export interface FolderTreeNode extends FetchedFolder {
  children: FolderTreeNode[];
}

function buildFolderTreeFromList(folders: FetchedFolder[]): FolderTreeNode[] {
  const folderMap = new Map<number, FolderTreeNode>();
  const tree: FolderTreeNode[] = [];

  // First pass: create all nodes
  folders.forEach((folder) => {
    folderMap.set(folder.id, { ...folder, children: [] });
  });

  // Second pass: build the tree structure
  folders.forEach((folder) => {
    const node = folderMap.get(folder.id)!;

    if (folder.parent_id && folderMap.has(folder.parent_id)) {
      // Add to parent's children
      const parent = folderMap.get(folder.parent_id)!;
      parent.children.push(node);
    } else {
      // Root level folder
      tree.push(node);
    }
  });

  // Sort tree at root level alphabetically by folder name (case-insensitive)
  tree.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  // Recursively sort children at each level
  const sortChildren = (nodes: FolderTreeNode[]) => {
    nodes.forEach((node) => {
      if (node.children.length > 0) {
        node.children.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
        sortChildren(node.children);
      }
    });
  };

  sortChildren(tree);

  return tree;
}

export interface FoldersSlice {
  folders: {
    main: FetchedFolder[];
    tree: FolderTreeNode[];
    addFolder: (folder: AddFolder) => Promise<AddFolderRes | undefined>;
    deleteFolder: (folderId: number) => Promise<void>;
    fetchFolders: () => Promise<FetchedFolder[]>;
    fetchFolderById: (folderId: number) => Promise<FetchedFolder | undefined>;
    removeFolder: (folderId: number) => void;
    updateFolder: (
      folderId: number,
      updatedFolder: Partial<Omit<FetchedFolder, "id">>
    ) => Promise<UpdateFolderRes | null>;
  };
}

const createFoldersSlice: StateCreator<BoundStore, [], [], FoldersSlice> = (set, get) => ({
  folders: {
    main: [],
    tree: [],
    addFolder: async (folder) => {
      const existingMainFolders = get().folders.main;
      const tempId = Date.now();

      return await performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => {
            const newFolders = sortFoldersAlphabetically([
              {
                ...folder,
                id: tempId,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                encrypted_data: "",
                iv: "",
              },
              ...state.folders.main,
            ]);
            return {
              ...state,
              folders: {
                ...state.folders,
                main: newFolders,
                tree: buildFolderTreeFromList(newFolders),
              },
            };
          }),
        apiCall: async () => {
          const addedFolder = await fetchers.folders.addFolder(folder);
          set((state) => {
            const updatedFolders = sortFoldersAlphabetically(
              state.folders.main.map((f) => (f.id === tempId ? { ...addedFolder, id: addedFolder.id } : f))
            );
            return {
              folders: {
                ...state.folders,
                main: updatedFolders,
                tree: buildFolderTreeFromList(updatedFolders),
              },
            };
          });

          return addedFolder;
        },
        rollback: () =>
          set((state) => ({
            ...state,
            folders: {
              ...state.folders,
              main: existingMainFolders,
              tree: buildFolderTreeFromList(existingMainFolders),
            },
          })),
      });
    },

    deleteFolder: async (folderId) => {
      const existingMainFolders = get().folders.main;
      const descendantIds = getDescendantFolderIds(existingMainFolders, folderId);
      const foldersToDelete = [folderId, ...descendantIds];
      const notesToDelete = get().notes.main.filter((note) => foldersToDelete.includes(note.folder_id!));

      await performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => {
            const filteredFolders = sortFoldersAlphabetically(
              state.folders.main.filter((folder) => !foldersToDelete.includes(folder.id))
            );
            const updatedMainNotes = state.notes.main.filter((note) => !foldersToDelete.includes(note.folder_id!));
            const updatedDeletedNotes = [
              ...state.notes.deleted,
              ...notesToDelete.map((note) => ({ ...note, folder_id: null })),
            ];
            return {
              folders: {
                ...state.folders,
                main: filteredFolders,
                tree: buildFolderTreeFromList(filteredFolders),
              },
              notes: {
                ...state.notes,
                main: updatedMainNotes,
                deleted: updatedDeletedNotes,
              },
            };
          }),
        apiCall: async () => {
          // Temp delete all notes in the folder tree
          await Promise.all(notesToDelete.map((note) => fetchers.notes.tempDeleteNote(note.id)));
          // Then delete all folders
          await Promise.all(foldersToDelete.map((id) => fetchers.folders.deleteFolder(id)));
        },
        rollback: () =>
          set((state) => ({
            folders: {
              ...state.folders,
              main: existingMainFolders,
              tree: buildFolderTreeFromList(existingMainFolders),
            },
            notes: {
              ...state.notes,
              main: [...state.notes.main, ...notesToDelete],
              deleted: state.notes.deleted.filter((note) => !notesToDelete.some((n) => n.id === note.id)),
            },
          })),
      });
    },

    fetchFolders: async () => {
      try {
        const folders = await fetchers.folders.fetchFolders();
        const sortedFolders = sortFoldersAlphabetically(folders);
        set((state) => ({
          folders: {
            ...state.folders,
            main: sortedFolders,
            tree: buildFolderTreeFromList(sortedFolders),
          },
        }));
        return folders;
      } catch (error) {
        console.error("Failed to fetch folders:", error);
        return [];
      }
    },

    fetchFolderById: async (folderId) => {
      try {
        const folder = await fetchers.folders.fetchFolderById(folderId);
        if (!folder) return undefined;

        set((state) => {
          const updatedFolders = sortFoldersAlphabetically(
            state.folders.main.map((f) => (f.id === folderId ? folder : f))
          );
          return {
            folders: {
              ...state.folders,
              main: updatedFolders,
              tree: buildFolderTreeFromList(updatedFolders),
            },
          };
        });
        return folder;
      } catch (error) {
        console.error("Failed to fetch folder by ID:", error);
        return undefined;
      }
    },

    removeFolder: (folderId) =>
      set((state) => {
        const filteredFolders = sortFoldersAlphabetically(
          state.folders.main.filter((folder) => folder.id !== folderId)
        );
        return {
          folders: {
            ...state.folders,
            main: filteredFolders,
            tree: buildFolderTreeFromList(filteredFolders),
          },
        };
      }),

    updateFolder: async (folderId, updatedFolder) => {
      const existingMainFolders = get().folders.main;
      const previousFolder = existingMainFolders.find((folder) => folder.id === folderId);

      if (!previousFolder) return null;

      return await performOptimisticAction({
        optimisticUpdate: () =>
          set((state) => {
            const updatedFolders = sortFoldersAlphabetically(
              state.folders.main.map((folder) =>
                folder.id === folderId ? { ...folder, ...updatedFolder, updated_at: new Date().toISOString() } : folder
              )
            );
            return {
              folders: {
                ...state.folders,
                main: updatedFolders,
                tree: buildFolderTreeFromList(updatedFolders),
              },
            };
          }),
        apiCall: async () => fetchers.folders.updateFolder({ id: folderId, folder: updatedFolder }),
        rollback: () =>
          set((state) => ({
            folders: {
              ...state.folders,
              main: existingMainFolders,
              tree: buildFolderTreeFromList(existingMainFolders),
            },
          })),
      });
    },
  },
});

function sortFoldersAlphabetically(folders: FetchedFolder[]): FetchedFolder[] {
  return [...folders].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

export default createFoldersSlice;
